import React, { useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'hooks/store';
import SectionBasePage from 'components/SectionBasePage';
import {
  clientsEntitiesSelector,
  isLoadingLocationsSelector,
  locationsEntitiesSelector,
  locationsIdsSelector,
  updateClientAction,
} from 'state/slices/clients';
import { useParams, useRouteMatch } from 'react-router';
import { Button } from 'UI';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { Form, TablePaginationConfig } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { UpdateClientData } from 'api/client';
import { StateEnum } from 'constants/state';
import { PlusOutlined } from '@ant-design/icons';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { ClientLocation } from 'constants/interface';
import { PaginationInternalData } from 'hooks/pagination';
import { ROUTES } from 'constants/routes';

import BasicUpdateClientForm, { BasicInfoUpdateClientFormData } from './components/BasicInfoUpdateClientForm';
import PaymentUpdateClientForm, { PaymentUpdateClientFormData } from './components/PaymentUpdateClientForm';
import LocationModal from './components/LocationModal';
import {
  MainContainer,
  PaymentCollapse,
} from './style';
import LocationClientList from './components/LocationClientList';

interface ClientDetailsPageRouteParams {
  clientId: string
}

interface ClientDetailsPageProps {
  locationPageData: PaginationInternalData
  onLocationTableChange: (
    pageData: TablePaginationConfig,
    filterData: Record<string, FilterValue | null>,
    sortData: SorterResult<
    ClientLocation> | SorterResult<ClientLocation>[],
    ) => void
  refreshLocations(): void
  onUpdateFinish?(): void
}

const ClientDetailsPage = ({
  onUpdateFinish,
  locationPageData,
  refreshLocations,
  onLocationTableChange,
}: ClientDetailsPageProps): JSX.Element => {
  const { clientId } = useParams<ClientDetailsPageRouteParams>();
  const clientsEntities = useSelector(clientsEntitiesSelector);
  const client = clientsEntities[clientId];

  const [basicInfoForm] = Form.useForm<BasicInfoUpdateClientFormData>();
  const [paymentInfoForm] = Form.useForm<PaymentUpdateClientFormData>();
  const [isLoading, setLoading] = useState(false);
  const [isBasicInfoUpdated, setBasicInfoUpdated] = useState(false);
  const [isPaymentInfoUpdated, setPaymentInfoUpdated] = useState(false);
  const navigator = useBreadcrumbNavigation();
  const dispatch = useDispatch();
  const { url } = useRouteMatch();

  const onSaveClick = async () => {
    try {
      await basicInfoForm.validateFields();
      await paymentInfoForm.validateFields();
    } catch (err) {
      const errorAlert = createAlertNotification('Verifique os campos com erro.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      return;
    }

    const basicData = basicInfoForm.getFieldsValue();
    const paymentData = paymentInfoForm.getFieldsValue();
    const clientFullData: UpdateClientData = {
      id: clientId,
      data: {
        ...basicData,
        ...paymentData,
        paymentMethod: paymentData.paymentMethod || null,
        paymentIsUpfront: paymentData.paymentIsUpfront === 'true',
        paymentCategory: paymentData.paymentCategory || null,
        isActive: true,
      },
    };

    try {
      setLoading(true);
      const result = await dispatch(updateClientAction(clientFullData));
      await unwrapResult(result);
      const successAlert = createAlertNotification(`
        Cliente atualizado com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      if (onUpdateFinish) onUpdateFinish();
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar atualizar cliente. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onBasicInfoFormChange = (_: unknown, data: BasicInfoUpdateClientFormData) => {
    const basicClientInfo: BasicInfoUpdateClientFormData = {
      name: client?.name,
      city: client?.city,
      state: client?.state,
      address: client?.address,
      phone: client?.phone,
      email: client?.email,
      cpf: client?.cpf,
      cnpj: client?.cnpj,
      stateRegistration: client?.stateRegistration,
      notes: client?.notes,
      // isActive: client?.isActive,
    };
    if (JSON.stringify(data) === JSON.stringify(basicClientInfo)) {
      setBasicInfoUpdated(false);
    } else {
      setBasicInfoUpdated(true);
    }
  };

  const onPaymentInfoFormChange = (_: unknown, data: PaymentUpdateClientFormData) => {
    const paymentInfo = {
      paymentCategory: client?.paymentCategory,
      paymentIsUpfront: client?.paymentIsUpfront,
      paymentMethod: client?.paymentMethod,
    };

    const formData = {
      paymentCategory: data?.paymentCategory || null,
      paymentIsUpfront: data?.paymentIsUpfront === 'true',
      paymentMethod: data?.paymentMethod || null,
    };
    if (JSON.stringify(formData) === JSON.stringify(paymentInfo)) {
      setPaymentInfoUpdated(false);
    } else {
      setPaymentInfoUpdated(true);
    }
  };

  // Location modal
  const [isLocationModalVisible, setLocationModalVisible] = useState(false);

  const closeModal = () => {
    // locationInfoForm.resetFields();
    setLocationModalVisible(false);
  };

  const onLocationAddSuccess = () => {
    const successAlert = createAlertNotification(`
      Cliente atualizado com sucesso.
    `, AlertNotificationType.Success);
    dispatch(setNotificationAction(successAlert));
    closeModal();
    refreshLocations();
  };

  // Locations page content list
  const locationsEntities = useSelector(locationsEntitiesSelector);
  const locationsIds = useSelector(locationsIdsSelector);
  const isLoadingLocations = useSelector(isLoadingLocationsSelector);
  const breadcrumbNavigator = useBreadcrumbNavigation();

  const clientLocations = locationsIds.map((locationId) => {
    const entity = locationsEntities[locationId];
    if (!entity) return undefined;
    const tableClientLocation: ClientLocation & {
      key?: string,
    } = {
      ...entity,
      key: locationId.toString(),
    };
    return tableClientLocation;
  }).filter((tableClient): tableClient is ClientLocation => !!tableClient);

  return (
    <SectionBasePage
      title={client?.name || 'Detalhes'}
      extra={(
        <Button
          loading={isLoading}
          onClick={onSaveClick}
          disabled={!isBasicInfoUpdated && !isPaymentInfoUpdated}
        >
          Salvar edição
        </Button>
      )}
    >
      {client && (
        <>
          <MainContainer>
            {/* BASIC INFO */}
            <BasicUpdateClientForm
              data={{
                ...client,
                state: client?.state || StateEnum.AC,
              }}
              onDataChange={onBasicInfoFormChange}
              formHandler={basicInfoForm}
              isLoading={isLoading}
            />
          </MainContainer>

          {/* PAYMENT INFO */}
          <PaymentCollapse ghost defaultActiveKey="payment">
            <PaymentCollapse.Panel header="Informações de pagamento" key="payment">
              <PaymentUpdateClientForm
                formHandler={paymentInfoForm}
                isLoading={isLoading}
                data={{
                  ...client,
                  paymentIsUpfront: String(client.paymentIsUpfront) || 'true',
                }}
                onDataChange={onPaymentInfoFormChange}
              />
            </PaymentCollapse.Panel>
          </PaymentCollapse>

          {/* LOCATIONS INFO */}
          <LocationModal
            // formHandler={locationInfoForm}
            clientId={clientId}
            onCancel={closeModal}
            onFinish={onLocationAddSuccess}
            // onSubmit={onModalSubmit}
            isVisible={isLocationModalVisible}
          />
          <PaymentCollapse ghost defaultActiveKey="locations">
            <PaymentCollapse.Panel
              header="Localizações"
              key="locations"
              extra={(
                <Button
                  icon={<PlusOutlined size={14} />}
                  disabled={isLoading}
                  onClick={(e) => {
                    setLocationModalVisible(true);
                    e.stopPropagation();
                  }}
                >
                  Adicionar localização
                </Button>
              )}
            >
              <LocationClientList
                locations={clientLocations}
                pageData={locationPageData}
                onTableChange={onLocationTableChange}
                isLoading={isLoadingLocations}
                onDetailsClick={(locationId) => breadcrumbNavigator.push({
                  label: locationsEntities[locationId]?.name || 'Localização',
                  url: `${url}${ROUTES.dashboard.clients.detail.locations.root}/${locationId}`,
                })}
              />
            </PaymentCollapse.Panel>
          </PaymentCollapse>
        </>
      )}
    </SectionBasePage>
  );
};

ClientDetailsPage.defaultProps = {
  onUpdateFinish: () => {},
};

export default ClientDetailsPage;
